import type {
  Split,
  SplitData,
  SplitsPayloadData,
  SplitWithException,
} from 'src/types/split';
import type {SimpleABTVariable} from 'src/components/lib/automation-module-registry/automation-module-registry';
import {
  TEMP_END_NODE_IDENTIFIER,
  TEMP_NODE_IDENTIFIER,
} from 'src/utils/automation-workflow';
import {
  createRuleHash,
  createRulesHash,
  getScopeFromActionRule,
  isRuleValueEmpty,
  normalizeAbtRule,
  normalizeEMSRule,
  parseEMSRule,
  parseActionRule,
  convertDateHoursMinutesToSeconds,
  checkIfModuleAllowsUserSelection,
  getPrimaryABTRuleInfo,
  getABTEventGroup,
  extractFieldFromABTRuleConditions,
  getUserSelectionReferenceFieldNameForEvent,
  userSelectionReferenceFieldNameRequiresValidation,
} from 'src/utils/filter';
import i18n from 'src/components/lib/i18n/new-i18n';
import type {
  EMSRule,
  FilterRule,
  OperatorOptions,
  PreviousRule,
  ReqResRuleType,
} from 'src/types/filter';
import uniqueId from 'lodash/uniqueId';
import type {
  DeleteNodeUsageWithId,
  Swimlane,
  Transition,
} from 'src/types/automation-workflow';


export const LAST_SPLIT_NAME = 'Else (Rest of all)';

export const DEFAULT_OPERATOR_OPTION = {
  string: 'eq',
  number: 'eq',
  date: 'eq',
  boolean: true,
  email: 'eq',
  phone: 'eq',
} as const;

export const getOperatorOptions = (
  t: (arg1: string, arg2: string) => string,
): OperatorOptions => {
  return {
    string: [
      {
        value: 'eq',
        label: t('is', 'is'),
        tooltip: `${t('status', 'status')} = "${t('placed', 'placed')}"`,
      },
    ],
    number: [
      {
        value: 'eq',
        label: t('equals', 'equals'),
        tooltip: `${t('value', 'value')} = 5`,
      },
    ],
    currency: [
      {
        value: 'eq',
        label: t('equals', 'equals'),
        tooltip: `${t('value', 'value')} = 5`,
      },
    ],
    date: [
      {
        value: 'eq',
        label: t('is_equal_to', 'is equal to'),
        tooltip: `${t('date_added', 'date_added')} = 2023-06-09`,
      },
    ],

    boolean: [
      {
        value: 'true',
        label: t('is_true', 'is true'),
        tooltip: `${t('candidate', 'candidate')}/${t('active', 'active')} ${t(
          'is_true',
          'is true',
        )}`,
      },
      {
        value: 'false',
        label: t('is_false', 'is false'),
        tooltip: `${t('candidate', 'candidate')}/${t('active', 'active')} ${t(
          'is_false',
          'is false',
        )}`,
      },
    ],
    email: [
      {
        value: 'eq',
        label: t('is', 'is'),
        tooltip: `${t('status', 'status')} = "${t('placed', 'placed')}"`,
      },
    ],
    phone: [
      {
        value: 'eq',
        label: t('is', 'is'),
        tooltip: `${t('number', 'number')} = "+12123231324"`,
      },
    ],
  };
};

export const MAX_SPLITS = 7;

export const MAX_RULES_IN_SPLIT = 7;

export const getCombinedId = (swid: string, nodeId: string): string => {
  return `${swid};${nodeId}`;
};

export const generateTempSplitNode = ({
  swid,
  ifEndNodeId,
  elseEndNodeId,
  if_display_name,
  else_display_name,
  t,
}: {
  swid: string;
  ifEndNodeId: string;
  elseEndNodeId: string;
  if_display_name: string;
  else_display_name: string;
  t: (arg1: string, arg2: string) => string;
}): SplitData => {
  return {
    [`${swid};${TEMP_NODE_IDENTIFIER}`]: [
      {
        id: 'temp:if',
        transition_id: 0,
        display_name: if_display_name,
        block: {rules: [], scope: null},
        tid: ifEndNodeId,
      },
      {
        id: 'temp:else',
        transition_id: 0,
        display_name: else_display_name,
        block: {rules: [], scope: null},
        tid: elseEndNodeId,
      },
    ],
  };
};

export const checkIfSplitPathIsValid = (
  splitPath: string,
  splitArray: Array<Split>,
): boolean => {
  return splitArray.some(({id}) => id === splitPath);
};

const getCurrentSplitInfoFromPreviousSplit = (
  previousSplitId: string,
  t: (arg1: string, arg2: string) => string,
) => {
  //NOTE:(diwakersurya): this is the first split (If)
  //if it is created client side, it will have id as temp:if
  // if it is saved to the backend, it will have id as 0
  if (['temp:if', '0'].includes(previousSplitId)) {
    return {
      id: 'temp:elseif_1',
      display_name: t('Else_IF', 'Else IF') + '-1',
    };
  }

  //otherwise it will either be a number based on index if it has been saved
  // or a temp:elseif_<number> if it is created client side

  const postfixString = previousSplitId.startsWith('temp:elseif_')
    ? previousSplitId.split('_')[1]
    : previousSplitId;
  const postfixNumber = parseInt(postfixString, 10) + 1;
  const id = `temp:elseif_${String(postfixNumber)}`;
  const newTitle = t('Else_IF', 'Else IF') + '-' + String(postfixNumber);
  return {
    id,
    display_name: newTitle,
  };
};
export const getNewSplitIdAndName = (
  previousSplitId: string,
  t: (arg1: string, arg2: string) => string,
): {id: string; display_name: string} => {
  const {id, display_name} = getCurrentSplitInfoFromPreviousSplit(
    previousSplitId,
    t,
  );
  return {
    id,
    display_name,
  };
};

export const getCurrentSelectedIndex = (
  splitArray: Array<Split>,
  splitData?: Split | null,
): number => {
  if (!splitArray || !splitData) {
    return 0;
  }
  return splitArray.findIndex(({id}) => id === splitData.id);
};

export const getUniqueTempEndNode = (): string => {
  return uniqueId(TEMP_END_NODE_IDENTIFIER);
};

const isInvalidRulePresent = (
  rules: Array<FilterRule>,
  getAbtPickerVariableInfo: (
    field: string,
  ) => SimpleABTVariable | null | undefined,
  getAbtUtilVariableInfo: (
    arg1: string,
    entityType: string,
    id: string,
  ) => SimpleABTVariable | null | undefined,
) => {
  // @ts-expect-error - TS7030 - Not all code paths return a value.
  return rules.some((rule) => {
    if (rule.type === 'prevNode') {
      const primaryABTRuleInfo = getPrimaryABTRuleInfo(
        rule.conditions,
        getAbtPickerVariableInfo,
      );
      const eventGroup = getABTEventGroup(primaryABTRuleInfo?.entity_type);
      const eventKey = primaryABTRuleInfo?.key;
      const allowUserSelection = eventGroup
        ? checkIfModuleAllowsUserSelection(eventGroup, eventKey as string)
        : false;
      if (allowUserSelection) {
        const userSelectionReferenceFieldName = eventKey
          ? getUserSelectionReferenceFieldNameForEvent(
              eventGroup ?? '',
              eventKey,
            )
          : 'REFERENCE_ID';
        if (
          !userSelectionReferenceFieldNameRequiresValidation(
            userSelectionReferenceFieldName,
          )
        ) {
          //if validation is not required on user selection field, return false
          return false;
        }
        const referenceField = extractFieldFromABTRuleConditions(
          userSelectionReferenceFieldName,
          primaryABTRuleInfo,
          getAbtUtilVariableInfo,
        );
        const userSelectedValue = rule.conditions.find(
          (condition) => condition.field === referenceField,
        )?.value;

        // If the user has not selected a value (often happens when the dropdown
        // has no options for a link-clicked prefill), validation fails.
        if (userSelectedValue == null) {
          return true;
        }
        return false;
      }
      return false;
    }
    if (typeof rule.operator === 'boolean') {
      return false;
    }
    if (!rule.operator) {
      return true;
    }
    if (isRuleValueEmpty(rule)) {
      return true;
    }
  });
};

export const validateSplits = (
  currentSplitId: string | null | undefined,
  splitArray: Array<Split>,
  getAbtPickerVariableInfo: (
    field: string,
  ) => SimpleABTVariable | null | undefined,
  getAbtUtilVariableInfo: (
    arg1: string,
    entityType: string,
    id: string,
  ) => SimpleABTVariable | null | undefined,
): {
  currentSplitRuleHashCountMap: Map<string, number>;
  warningsArray: Array<string>;
  noRuleExists: boolean;
  splitsWithDuplicateRules: Array<string>;
  splitsWithInvalidRules: Array<string>;
  addedSplitsExceedMaximum: boolean;
  addedRulesExceedMaximum: boolean;
  anyScopeIsInvalid: boolean;
  anyScopeContainsInvalidTimespan: boolean;
} => {
  const currentSplit = splitArray.find(({id}) => id === currentSplitId);
  const currentSplitRuleHashCountMap =
    currentSplit?.block.rules.reduce(
      (acc: Map<string, number>, rule: FilterRule) => {
        const hash = createRuleHash(rule);
        const val = acc.get(hash);
        if (val) {
          return acc.set(hash, val + 1);
        }
        return acc.set(hash, 1);
      },
      new Map(),
    ) ?? new Map();
  //todo(vish): this logic can be simplified, take it later.
  const warnings: Map<string, Array<string>> = new Map();
  for (let i = 0; i < splitArray.length; i++) {
    if (splitArray[i].block.rules.length > 0) {
      const outerRulesHash = createRulesHash(splitArray[i].block.rules);
      for (let j = i + 1; j < splitArray.length; j++) {
        const innerRulesHash = createRulesHash(splitArray[j].block.rules);
        if (outerRulesHash === innerRulesHash) {
          if (!warnings.has(splitArray[i].display_name)) {
            warnings.set(splitArray[i].display_name, []);
          }
          warnings.set(splitArray[i].display_name, [
            ...(warnings.get(splitArray[i].display_name) ?? []),
            splitArray[j].display_name,
          ]);
        }
      }
    }
  }
  const condensedGroups = condenseRules(warnings);
  const warningsArray = condensedGroups.map((group) => {
    const joinedGroups = group.join(', ');
    return `${joinedGroups} are having the same rules. Change value to add this split.`;
  });

  const noRuleExists =
    splitArray && splitArray[0] && splitArray[0].block.rules.length === 0;

  function isDuplicateRulePresent(rules: Array<FilterRule>) {
    const hashCountMap = rules.reduce((acc, rule) => {
      const hash = createRuleHash(rule);
      const val = acc.get(hash);
      if (val) {
        return acc.set(hash, val + 1);
      }
      return acc.set(hash, 1);
    }, new Map());
    return [...hashCountMap.values()].some((count) => count > 1);
  }

  const splitsWithDuplicateRules = splitArray.reduce<Array<any>>(
    (acc, split) => {
      if (isDuplicateRulePresent(split.block.rules)) {
        acc.push(split.display_name);
      }
      return acc;
    },
    [],
  );

  const splitsWithInvalidRules = splitArray.reduce<Array<any>>((acc, split) => {
    if (
      isInvalidRulePresent(
        split.block.rules,
        getAbtPickerVariableInfo,
        getAbtUtilVariableInfo,
      )
    ) {
      acc.push(split.display_name);
    }
    return acc;
  }, []);

  const addedSplitsExceedMaximum = currentSplit
    ? splitArray.length > MAX_SPLITS
    : false;

  const addedRulesExceedMaximum =
    splitArray &&
    splitArray[0] &&
    splitArray[0].block.rules.length > MAX_RULES_IN_SPLIT;

  const anyScopeIsInvalid = splitArray.some(({block}) => {
    if (!block.scope) {
      return false;
    }
    return (
      block.scope.direction === 'next' &&
      (block.scope.timespan == null ||
        (block.scope.timespan.days === 0 &&
          block.scope.timespan.hours === 0 &&
          block.scope.timespan.minutes === 0))
    );
  });

  const anyScopeContainsInvalidTimespan = splitArray.some(({block}) => {
    if (!block.scope || block.scope.direction === 'indefinitely') {
      return false;
    }
    const timespan = block.scope.timespan;
    return timespan == null || timespan.hours > 23 || timespan.minutes > 59;
  });

  return {
    currentSplitRuleHashCountMap,
    warningsArray,
    noRuleExists,
    splitsWithDuplicateRules,
    splitsWithInvalidRules,
    addedSplitsExceedMaximum,
    addedRulesExceedMaximum,
    anyScopeIsInvalid,
    anyScopeContainsInvalidTimespan,
  };
};

function condenseRules(ruleMap: Map<string, Array<string>>) {
  const groups: Array<Array<string>> = [];

  function findGroup(id: string) {
    return groups.find((group) => group.includes(id));
  }

  ruleMap.forEach((value, key) => {
    const existingGroup = findGroup(key);
    if (existingGroup) {
      value.forEach((relatedId) => {
        if (!existingGroup.includes(relatedId)) {
          existingGroup.push(relatedId);
          const relatedGroup = findGroup(relatedId);
          if (relatedGroup) {
            relatedGroup.forEach((id) => {
              if (!existingGroup.includes(id)) {
                existingGroup.push(id);
              }
            });
            groups.splice(groups.indexOf(relatedGroup), 1);
          }
        }
      });
    } else {
      const newGroup = [key, ...value];
      groups.push(newGroup);
    }
  });

  return groups;
}

export const getSplitsPayload = (
  splitArray: Array<Split>,
  from_node_id: string,
): Array<SplitsPayloadData> => {
  const splitsData = splitArray.reduce<Array<any>>((outerAcc, {block, tid}) => {
    const conditions = block.rules.reduce<Array<any>>((acc, rule) => {
      const normalizedRule = ['ems', 'reqRes'].includes(rule.type as string)
        ? // @ts-expect-error - TS2345 - Argument of type 'EMSRule | PreviousRule' is not assignable to parameter of type 'EMSRule'.
          normalizeEMSRule(rule)
        : // @ts-expect-error - TS2345 - Argument of type 'EMSRule | PreviousRule' is not assignable to parameter of type 'PreviousRule'.
          normalizeAbtRule(rule, block.scope);
      if (normalizedRule) {
        if (['ems', 'reqRes'].includes(rule.type as string)) {
          acc.push({conditions: [normalizedRule]});
        } else {
          acc.push({conditions: normalizedRule});
        }
      }
      return acc;
    }, []);
    const obj: Record<string, any> = {};
    obj.conditions = {
      blocks: [
        {
          rules: conditions,
        },
      ],
    };
    obj.from_node_id = from_node_id;
    obj.to_node_id = tid.startsWith(TEMP_END_NODE_IDENTIFIER) ? null : tid;
    outerAcc.push(obj);
    return outerAcc;
  }, []);
  // This part is a bit weird but we need to add an end date field
  // to the last split of splits array ,if we have an abt rule, because backend requires it
  const scope = splitArray[0].block.scope;
  if (scope != null) {
    const abtRule = splitArray[0].block.rules.find(
      ({type}) => type === 'prevNode',
    );
    if (abtRule && abtRule.type === 'prevNode') {
      const endDateField = abtRule.conditions.find(({field}) =>
        field.includes('.end_date'),
      )?.field;
      let offset, endValue;
      if (scope && scope.timespan == null) {
        offset = 0;
        endValue = 'plus_infinity';
      } else if (scope.timespan) {
        const {days, hours, minutes} = scope.timespan;
        const offsetInSeconds = convertDateHoursMinutesToSeconds(
          days,
          hours,
          minutes,
        );
        offset = offsetInSeconds;
        endValue = 'now';
      }
      splitsData[splitsData.length - 1].conditions.blocks[0].rules.push({
        conditions: [
          {
            field: endDateField,
            value: endValue,
            offset,
            operator: 'gt',
          },
        ],
      });
    }
  }

  return splitsData;
};

export const generateTitleFromIndex = (
  index: number,
  totalTransitions: number,
) => {
  if (index === 0) {
    return i18n.t('If', 'If');
  }
  if (index === totalTransitions - 1) {
    return i18n.t(
      `${i18n.t('Else', 'Else')} (${i18n.t('Rest_of_all', 'Rest of all')})`,
      'Else (Rest of all)',
    );
  }
  return `${i18n.t('Else_IF', 'Else IF')}-${index}`;
};

export const generateSplitsArray = (
  transitions: Array<Transition>,
): Array<Split> => {
  // @ts-expect-error - TS7034 - Variable 'scope' implicitly has type 'any' in some locations where its type cannot be determined.
  let scope = null;
  let scopeParsed = false;
  return transitions.map((transition, index) => {
    const rules: Array<EMSRule | ReqResRuleType | PreviousRule> = [];
    (transition.condition.blocks[0].rules ?? []).forEach((rule) => {
      //There shouldn't be any rules for else
      if (rule.conditions.length === 0) {
        return;
      }
      if (rule.conditions.length > 1) {
        // @ts-expect-error
        rules.push(parseActionRule(rule.conditions));
        if (!scopeParsed) {
          // @ts-expect-error
          scope = getScopeFromActionRule(rule.conditions);
          scopeParsed = true;
        }
      } else {
        // @ts-expect-error
        rules.push(parseEMSRule(rule.conditions[0]));
      }
    });
    return {
      id: String(index),
      title: generateTitleFromIndex(index, transitions.length),
      transition_id: transition.transition_id ?? null,
      display_name: transition.display_name ?? '',
      tid: transition.next_node,
      block: {
        rules: index === transitions.length - 1 ? [] : rules,
        // @ts-expect-error - TS7005 - Variable 'scope' implicitly has an 'any' type.
        scope,
      },
    };
  });
};

export const parseSplits = (swimlanes: Array<Swimlane>): SplitData => {
  return swimlanes.reduce<Record<string, any>>((acc, {id, nodes}) => {
    const swimlaneId = id;

    Object.keys(nodes).forEach((nodeId) => {
      const isSplitNode = nodes[nodeId].body.composite_node_type === 'split';
      if (isSplitNode) {
        const combinedSwidSplitId = `${swimlaneId};${nodeId}`;
        acc[combinedSwidSplitId] = generateSplitsArray(
          nodes[nodeId].next_nodes,
        );
      }
    });
    return acc;
  }, {});
};

export const appendExceptionsToSplits = (
  splitArray: Array<Split>,
  hfDeleteInfo: Array<DeleteNodeUsageWithId>,
): Array<SplitWithException> => {
  return splitArray.map((split, index) => {
    const splitDeleteInfo = hfDeleteInfo.find(
      ({id}) => parseInt(id, 10) === index,
    );
    if (splitDeleteInfo) {
      const hfmodeExceptions = Object.keys(splitDeleteInfo).filter(
        (key) =>
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeleteNodeUsageWithId'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DeleteNodeUsageWithId'.
          typeof splitDeleteInfo[key] === 'boolean' && splitDeleteInfo[key],
      );
      return {
        ...split,
        hfmodeExceptions,
      };
    }
    return {
      ...split,
      hfmodeExceptions: [],
    };
  });
};
